import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import tools from "../../../../../../../helpers/tools";
import LabelValuePair from "../../../../../../common/LabelValuePair/LabelValuePair";
import LegalFormValue from "../../../../../../common/LegalFormValue/LegalFormValue";
import BottomPanel from "./BottomPanel/BottomPanel";
import { CloseCompanyBottomPanelButton } from "./BottomPanel/CompanyBottomPanel/CompanyBottomPanel";
import CompanyPoliciesRecap from "./CompanyPoliciesRecap/CompanyPoliciesRecap";
import useCompanyItem from "./useCompanyItem";
import CompanyStatuses from "../../../../../../common/CompanyInfo/CompanyStatuses";
const queryString = require("query-string");

const POLICY_STATUS_CANCELED = [20, 30]; // 20 = Suspendu, 30 = Résilié

// Statut global de la société, dérivé de ses contrats. Pilote la couleur
// du bandeau d'accent pour scanner l'état d'un coup d'œil.
const getCompanyStatus = (company) => {
  if (company.isBlackListed || company.isDelisted) {
    return { key: "alert", color: "var(--ds-danger)", label: "Signalée" };
  }
  const policies = company.policies || [];
  if (policies.length === 0) {
    return {
      key: "empty",
      color: "var(--ds-slate-300)",
      label: "Aucun contrat",
    };
  }
  const valid = policies.filter(
    (p) => !POLICY_STATUS_CANCELED.includes(p.status),
  );
  if (valid.length === 0) {
    return { key: "canceled", color: "var(--ds-danger)", label: "Tout résilié" };
  }
  if (valid.length < policies.length) {
    return {
      key: "partial",
      color: "var(--ds-warning)",
      label: "Partiellement résilié",
    };
  }
  return { key: "active", color: "var(--ds-success)", label: "Actif" };
};

const CompanyItem = ({ companyData }) => {
  const { company, isDeleted, isDeleting, actions } = useCompanyItem();
  const location = useLocation();

  useEffect(() => {
    actions.initCompany(companyData);
  }, []);

  useEffect(() => {
    if (company?.id) {
      let parsed = queryString.parse(location.search, {
        arrayFormat: "bracket",
      });
      if (parsed?.panel && company.siret.includes(parsed.sirenOuSiret)) {
        setTimeout(() => {
          actions.openBottomPanel(parsed.panel);
        }, 1000);
      }
    }
  }, [company?.id]);

  if (!company) return null;

  const status = getCompanyStatus(company);
  const nbPolicies = company.policies?.length || 0;

  const infos = [
    `${tools.getFullAddress(company.location1)}`,
    `${company.ape || ""} - ${company.companyActivity || ""}`,
    <LabelValuePair
      label="Forme juridique"
      value={<LegalFormValue legalForm={company.legalForm} />}
    />,
    <LabelValuePair
      label="CA"
      value={`${tools.truenumberWithSpaces(company.turnover)} €`}
    />,
    <LabelValuePair
      label="Immatriculée le"
      value={tools.formatDate(company.registrationAt)}
    />,
    <LabelValuePair
      label="Effectif"
      value={
        company.workforce === 0
          ? "0"
          : tools.truenumberWithSpaces(company.workforce)
      }
    />,
  ];

  return (
    <>
      <CompanyContainer
        isDeleted={isDeleted}
        accent={status.color}
        className="position-relative animated fadeIn"
        id={`leaditem${company.id}`}
      >
        {/* HEADER : nom (ouvre le panneau infos) + SIRET + statuts métier */}
        <div className="company-card__header">
          <button
            type="button"
            onClick={() => actions.openBottomPanel("COMPANY")}
            className="company-card__name"
            title="Voir les informations de la société"
          >
            <span>{company.company}</span>
            <i className="fa fa-chevron-right" />
          </button>
          <div className="company-card__siret">
            {tools.formatFollowingPattern(company.siret, [3, 3, 3, 5], " ")}
          </div>
          <div className="company-card__statuses">
            <CompanyStatuses company={company} />
          </div>
        </div>

        {/* CORPS : KPI contrats + badges produits (via CompanyPoliciesRecap) */}
        <div className="company-card__body">
          <div className="company-card__kpi">
            <span className="company-card__kpi-value">{nbPolicies}</span>
            <span className="company-card__kpi-label">
              contrat{nbPolicies > 1 ? "s" : ""}
            </span>
          </div>
          <CompanyPoliciesRecap />
        </div>

        {/* FOOTER : détails repliables (Résumé) + action destructive isolée */}
        <details className="company-card__footer">
          <summary className="company-card__summary">
            <i className="fa fa-info-circle mr-1" />
            Détails de la société
          </summary>
          <div className="company-card__details">
            {infos.map((info, ik) => (
              <div
                className="company-card__detail-item"
                key={`inf${ik}`}
                title={typeof info == "string" ? info : ""}
              >
                {info}
              </div>
            ))}
            <button
              type="button"
              className="company-card__delete"
              onClick={actions.onRemoveCompany}
              data-priv="bo_deleteclient"
            >
              <i className="fa fa-trash mr-1" />
              {isDeleting ? "Suppression en cours…" : "Supprimer cette société"}
            </button>
          </div>
        </details>

        <CloseCompanyBottomPanelButton />
      </CompanyContainer>
      <BottomPanel />
    </>
  );
};

const CompanyContainer = styled.div`
  filter: ${({ isDeleted }) => (isDeleted ? "grayscale(1)" : "none")};
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid var(--ds-slate-200);
  border-left: 4px solid ${({ accent }) => accent};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  transition: box-shadow 0.18s ease, transform 0.18s ease,
    border-color 0.18s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
    border-color: var(--ds-slate-300);
    border-left-color: ${({ accent }) => accent};
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }

  /* ---- HEADER ---- */
  & .company-card__header {
    padding: 10px 12px 8px;
    border-bottom: 1px solid var(--ds-slate-100);
  }

  & .company-card__name {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.25;
    color: var(--ds-slate-900);
    cursor: pointer;
    transition: color 0.15s ease;

    & i {
      flex-shrink: 0;
      font-size: 11px;
      color: var(--ds-slate-400);
      transition: transform 0.15s ease, color 0.15s ease;
    }

    &:hover {
      color: var(--ds-blue);
      & i {
        color: var(--ds-blue);
        transform: translateX(2px);
      }
    }
  }

  & .company-card__siret {
    margin-top: 2px;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
    color: var(--ds-slate-500);
  }

  & .company-card__statuses {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;

    &:empty {
      display: none;
    }
  }

  /* ---- BODY ---- */
  & .company-card__body {
    flex: 1 1 auto;
    padding: 10px 12px;
  }

  & .company-card__kpi {
    display: flex;
    align-items: baseline;
    gap: 5px;
    margin-bottom: 8px;
  }

  & .company-card__kpi-value {
    font-size: 22px;
    font-weight: 800;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    color: var(--ds-slate-900);
  }

  & .company-card__kpi-label {
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--ds-slate-500);
  }

  /* ---- FOOTER ---- */
  & .company-card__footer {
    border-top: 1px solid var(--ds-slate-100);
    background: var(--ds-slate-50);
  }

  & .company-card__summary {
    list-style: none;
    cursor: pointer;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 600;
    color: var(--ds-slate-600);
    user-select: none;
    transition: color 0.15s ease;

    &::-webkit-details-marker {
      display: none;
    }
    &:hover {
      color: var(--ds-blue);
    }
  }

  & details[open] .company-card__summary {
    color: var(--ds-blue);
  }

  & .company-card__details {
    padding: 0 12px 10px;

    & .company-card__detail-item {
      font-size: 12px;
      line-height: 1.5;
      color: var(--ds-slate-700);
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  & .company-card__delete {
    margin-top: 8px;
    padding: 0;
    border: 0;
    background: transparent;
    font-size: 12px;
    font-weight: 500;
    color: var(--ds-danger);
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &::after {
    display: ${({ isDeleted }) => (isDeleted ? "flex" : "none")};
    content: "supprimée";
    width: 100%;
    height: 100%;
    color: white;
    position: absolute;
    z-index: 99;
    background: rgba(0, 0, 0, 0.32);
    top: 0;
    left: 0;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: bold;
  }
`;

export default CompanyItem;
